import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '../core-ui';
import DataStore from '../support/DataStore';
import createContext from '../support/helpers/createContext';
import { fetchJson } from '../support/useFetch';
import { Account } from '../types/shared/Account';
import { Season } from '../types/shared/Season';

import { useAuthState } from './AuthState';

export const SEASON_ID_KEY = 'app:season-id';

type Context = {
  season: Season | null;
  setSeason: (season: Season | null) => void;
};

const [useContext, Provider] = createContext<Context>();

const dataStore = DataStore.getInstance('app');

type SeasonResult = { success: true; season: Season } | { success: false };

async function getSeasonDetails(
  account: Account,
  seasonId: string | null,
): Promise<SeasonResult> {
  if (!seasonId || !account.token) {
    return { success: false };
  }
  if (seasonId === account.currentSeason) {
    return { success: false };
  }
  return await fetchJson(`/api/seasons/${seasonId}`, {
    headers: { 'X-Auth-Token': account.token ?? '' },
  });
}

export function SeasonStateProvider(props: { children: ReactNode }) {
  const { account } = useAuthState();
  const [season, setSeason] = useState<Season | null>(null);

  // Get initial seasonId only at mount
  const [initialSeasonId] = useState(() =>
    sessionStorage.getItem(SEASON_ID_KEY),
  );

  const { data, isLoading, error } = useQuery(
    ['get-season', initialSeasonId],
    () => getSeasonDetails(account, initialSeasonId),
    {
      onSuccess: (data) => {
        if (data.success) {
          dataStore.set('season', data.season);
        } else {
          dataStore.set('season', null);
        }
      },
    },
  );

  // The logic in this effect syncs changes from dataStore to state.
  useEffect(() => {
    const subscription = dataStore.onChange('season', (newSeason) => {
      if (newSeason) {
        const seasonId = newSeason.id;
        sessionStorage.setItem(SEASON_ID_KEY, seasonId);
      } else {
        sessionStorage.removeItem(SEASON_ID_KEY);
      }
      setSeason(newSeason ?? null);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const context = useMemo<Context>(
    () => ({
      season,
      setSeason: (season: Season | null) => {
        dataStore.set('season', season);
      },
    }),
    [season],
  );

  if (error) {
    return <p>{String(error)}</p>;
  }

  if (isLoading || !data) {
    return <LoadingIndicator />;
  }

  return <Provider value={context}>{props.children}</Provider>;
}

export function useSeason() {
  return useContext();
}
